import { Theme } from '@emotion/react';
import { CameraAlt, Close, QrCode } from '@mui/icons-material';
import {
  Divider,
  css,
  Dialog,
  DialogTitle,
  DialogContent,
  AvatarGroup,
  Typography,
  Box,
  DialogActions,
  IconButton,
  Skeleton,
  Button,
} from '@mui/material';
import { Fragment, useMemo } from 'react';

import { isBetween, parseDate } from '@jebel/utils';

import { OrganizationAvatar } from 'shared/components/symbols';
import { LoadingOverlay, MediaGallery } from 'shared/components/ui';
import { StreamActivityProvider, useStreamChatEnrichedActivity } from 'shared/features/posts';
import { EventInfoFragment } from 'shared/graphql';
import {
  useCurrentUser,
  useCurrentUserPermissions,
  useModalState,
  useStreamChatReactions,
} from 'shared/hooks';

import { EventAttendanceModal } from './EventAttendanceModal';
import { EventCodeModal } from './EventCodeModal';
import { EventComments } from './EventComments';
import { EventInfoModalDateLocation } from './EventInfoModalDateLocation';

export type EventInfoModalProps = {
  event: EventInfoFragment;

  show: boolean;

  onClose?: () => void;
};

export function EventDetailsModal({ event, show, onClose }: EventInfoModalProps) {
  const { user } = useCurrentUser();
  const { isCommunityAdministrator } = useCurrentUserPermissions();

  const { data: activity, loading: isActivityLoading } = useStreamChatEnrichedActivity({
    post: event,
  });

  const {
    show: showAttendanceModal,
    open: openAttendanceModal,
    close: closeAttendanceModal,
  } = useModalState();

  const {
    show: showEventCodeModal,
    open: openEventCodeModal,
    close: closeEventCodeModal,
  } = useModalState();

  const { onCommentAdd, onLikeAdd, onReactionRemove, onCommentRemove } = useStreamChatReactions();

  const images = event?.images?.items ?? [];
  const otherSponsors = event?.otherSponsors?.items ?? [];

  const canShowAttendanceCode = useMemo(() => {
    const isAuthor = event.author && event.author?.id === user?.id;
    const isCommunityAdministrated = !event.author && isCommunityAdministrator;

    return isAuthor || isCommunityAdministrated;
  }, [event.author, user?.id, isCommunityAdministrator]);

  const canMarkAttendance = useMemo(() => {
    const eventDate = parseDate(event.date);
    // Allow to mark attendance between the event start and 12 hours after.
    return isBetween(new Date(), { start: eventDate, end: eventDate.plus({ hours: 12 }) });
  }, [event.date]);

  return (
    <Dialog open={show} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle css={headerCSS}>
        <Typography variant="h6">{event.title}</Typography>

        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <StreamActivityProvider data={activity} loading={isActivityLoading}>
        <DialogContent css={contentCSS} dividers>
          <LoadingOverlay>
            <EventInfoModalDateLocation event={event!} />
          </LoadingOverlay>

          <Divider />

          <Box display="grid" gridTemplateColumns="4fr 1fr" gap={1}>
            <Box>
              <Typography css={descriptionTitleCss} color="primary">
                Description
              </Typography>

              {event.description ? (
                <Typography variant="body1">{event.description}</Typography>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  No description available
                </Typography>
              )}
            </Box>

            {event?.titleSponsor && (
              <Box display="flex" flexDirection="column" alignItems="center" gap="0.5rem">
                <Typography css={titleSponsorCss} color="textSecondary">
                  Title Sponsor
                </Typography>

                <OrganizationAvatar
                  variant="rounded"
                  size={96}
                  organization={event?.titleSponsor}
                />
              </Box>
            )}
          </Box>

          <MediaGallery media={images} />

          {otherSponsors.length > 0 && (
            <Fragment>
              <Typography css={titleSponsorCss} color="textSecondary">
                Other Sponsors
              </Typography>

              <AvatarGroup max={4} total={otherSponsors.length}>
                {otherSponsors.map((sponsor, index) => (
                  <OrganizationAvatar key={index} organization={sponsor} />
                ))}
              </AvatarGroup>
            </Fragment>
          )}

          {canShowAttendanceCode && (
            <>
              <Button
                disableElevation
                variant="contained"
                startIcon={<QrCode />}
                onClick={openEventCodeModal}
              >
                Show attendance code
              </Button>

              <EventCodeModal
                eventId={event.id}
                show={showEventCodeModal}
                onClose={closeEventCodeModal}
              />
            </>
          )}

          {canMarkAttendance && (
            <>
              <Button
                disableElevation
                variant="contained"
                startIcon={<CameraAlt />}
                onClick={openAttendanceModal}
              >
                Mark attendance
              </Button>

              <EventAttendanceModal show={showAttendanceModal} onClose={closeAttendanceModal} />
            </>
          )}
        </DialogContent>

        <DialogActions css={commentsCSS}>
          <LoadingOverlay
            overlay={<Skeleton variant="rectangular" width="100%" height="5rem" />}
            loading={isActivityLoading}
          >
            <EventComments
              onCommentAdd={onCommentAdd}
              onLikeAdd={onLikeAdd}
              onReactionRemove={onReactionRemove}
              onCommentRemove={onCommentRemove}
              activity={activity}
            />
          </LoadingOverlay>
        </DialogActions>
      </StreamActivityProvider>
    </Dialog>
  );
}

const headerCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const contentCSS = css`
  display: grid;
  grid-row-gap: 1rem;
`;

const descriptionTitleCss = css`
  font-weight: 500;
`;

const titleSponsorCss = (theme: Theme) => css`
  font-size: ${theme.typography.fontSize + 2}px;
  font-weight: 500;

  ${theme.breakpoints.down('md')} {
    font-size: ${theme.typography.fontSize}px;
  }
`;

const commentsCSS = css`
  display: flex;
  justify-items: start;
  align-items: stretch;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`;
